/* eslint-disable function-paren-newline */
import PropTypes from 'prop-types';
import React from 'react';
import { Mutation, Query } from '@apollo/client/react/components';

import BulkSegmentAllocator from './BulkSegmentAllocator';
import { BULK_UPSERT_DATA_ENTRY_SEGMENTS, GET_CLIENT_SEGMENT_CATEGORIES } from './queries';

/**
 * @param {Object} props Component props
 * @param {string} props.clientId
 * @param {string} props.ledgerAccountId The id of the ledger where transactions are being viewed
 * @param {Array.<Transaction>} props.transactions The transactions to bulk apply segments
 * @param {function} props.onComplete
 * @param {function(): void} props.onClose Called when the user closes bulk allocation
 * @returns {ReactNode}
 * @constructor
 */
export default function BulkSegmentAllocatorContainer({
  clientId,
  ledgerAccountId,
  transactions,
  onComplete,
  onClose,
}) {
  const mappedLedgerDataEntries = transactions.reduce((acc, transaction) => {
    const dataEntriesInSameLedger = transaction.dataEntries.filter(
      entry => entry.ledgerAccountId === ledgerAccountId,
    );
    return [...acc, ...dataEntriesInSameLedger];
  }, []);

  const dataEntryIds = mappedLedgerDataEntries.map(dataEntry => dataEntry.id);

  const hasSegmentedTransactions = transactions.some(
    transaction => transaction.hasSegmentAllocations,
  );

  return (
    <Query query={GET_CLIENT_SEGMENT_CATEGORIES} variables={{ clientId }}>
      {({ loading: loadingSegmentCategories, data: clientSegmentCategoryData }) => {
        if (loadingSegmentCategories) {
          return 'Loading...';
        }
        const clientSegmentCategories = clientSegmentCategoryData.client.segmentCategories;

        return (
          <Mutation mutation={BULK_UPSERT_DATA_ENTRY_SEGMENTS}>
            {bulkUpsertDataEntrySegments => (
              <BulkSegmentAllocator
                clientCategories={clientSegmentCategories}
                clientId={clientId}
                dataEntries={mappedLedgerDataEntries}
                isOverridingSegments={hasSegmentedTransactions}
                onCancel={onClose}
                onDone={onClose}
                onSave={categorySegmentSelections =>
                  bulkUpsertDataEntrySegments({
                    variables: {
                      data: {
                        categorySegmentSelections,
                        clientId,
                        dataEntryIds,
                      },
                    },
                    /**
                     * Poor man's method for invalidating DataEntrySegmentDetails
                     * cache records. This forces Apollo to re-fetch the corresponding
                     * cached query on the SegmentAllocator and load the DataEntrySegment
                     * records created or updated by this mutation server side.
                     *
                     * @see https://github.com/apollographql/apollo-feature-requests/issues/4
                     *
                     * @param {DataProxy} proxy Apollo's in-memory cache.
                     */
                    update: proxy => {
                      const dataEntryDetailsPattern = `dataEntrySegmentDetails.+"entryId":"(${dataEntryIds.join(
                        '|',
                      )})"`;
                      const reDataEntrySegmentDetailsEntry = new RegExp(dataEntryDetailsPattern);

                      Object.keys(proxy.data.data).forEach(key => {
                        if (key.match(reDataEntrySegmentDetailsEntry)) {
                          proxy.data.delete(key);
                        }
                      });
                    },
                  }).then(() => onComplete(transactions.map(transaction => transaction.id)))
                }
              />
            )}
          </Mutation>
        );
      }}
    </Query>
  );
}

BulkSegmentAllocatorContainer.propTypes = {
  clientId: PropTypes.string.isRequired,
  ledgerAccountId: PropTypes.string.isRequired,
  transactions: PropTypes.arrayOf(PropTypes.object).isRequired,
  onComplete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
